import { Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, Provider, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { FormsUtil, GuidEmpty, ModalService, ToastService } from 'mbs-ui-kit';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { noop } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { RestoreSourceStepFormValue, RestoreSourceStepValue } from 'wizards/models/restore-source-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { CheckboxLegendComponent } from 'wizards/steps/components/checkbox-legend/checkbox-legend.component';
import { FilesFoldersListComponent } from 'wizards/steps/components/files-folders-list/files-folders-list.component';
import { BaseForRestoreSourceOrDisksStep } from 'wizards/steps/only-restore-steps/base/base-for-restore-source-or-disks-step.directive';
import { FormPipeOperators } from 'wizards/steps/StepBase.class';

const RestoreSourceStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => RestoreSourceStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-restore-source-step',
  templateUrl: './restore-source-step.component.html',
  styleUrls: ['./restore-source-step.component.scss'],
  providers: [RestoreSourceStepValueAccessor]
})
export class RestoreSourceStepComponent
  extends BaseForRestoreSourceOrDisksStep<RestoreSourceStepValue>
  implements OnInit, OnChanges, OnDestroy
{
  @Input() mainLoading: boolean;

  public foldersForm = new UntypedFormGroup({ filesFoldersData: new FormControl({ enabledFields: true, paths: [] }) });

  private firstLoad = true;

  @ViewChild('fileFoldersComponent', { static: true, read: FilesFoldersListComponent }) fileFoldersComponent: FilesFoldersListComponent;

  public restoreSourceInfoAlert = this.i18nextService.t('wizards:restore_source_info_alert', { returnObjects: true });

  constructor(
    public i18nextService: I18NextService,
    public mainService: RemoteManagementWizardsService,
    public modal: ModalService,
    public stepService: WizardStepsService,
    public toastService: ToastService,
    private auth: AuthFacade
  ) {
    super(mainService, modal, stepService, toastService);
  }

  ngOnInit(): void {
    this.auth.currentUser$.pipe(untilDestroyed(this)).subscribe((user) => {
      this.allowedRecovery = user.ProviderInfo.PasswordRecoveryEnabled && user.IsProvider;
    });

    this.initForm();

    this.setValues();
  }

  protected getPipeOperators(): FormPipeOperators {
    return [untilDestroyed(this), debounceTime(200)];
  }

  initForm(): void {
    this.stepForm = new FormGroup<RestoreSourceStepFormValue>({ folders: new FormControl([], [Validators.required]) });

    this.subscribeToFoldersForm();
    this.initFormEvents();
  }

  subscribeToFoldersForm(): void {
    this.foldersForm.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value.filesFoldersData && value.filesFoldersData.paths && value.filesFoldersData.paths.length) {
        this.stepForm.get('folders').setValue(value.filesFoldersData.paths);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.bunchId?.currentValue && changes.bunchId.currentValue !== changes.bunchId.previousValue) {
      this.mainService.password.next(this.passwordsForBunches[changes.bunchId.currentValue] || '');
    }

    if (
      this.isRDMode ||
      (changes.computerName && !changes.computerName.currentValue) ||
      (changes.bunchId && !changes.bunchId.currentValue) ||
      (changes.storageId && (!changes.storageId.currentValue || changes.storageId.currentValue === GuidEmpty))
    ) {
      this.treeData = [];
    }

    if (!this.isRDMode && this.computerName && this.storageId && this.storageId !== GuidEmpty) {
      const bunchIsChanged = !this.isNBF && changes.bunchId?.currentValue && changes.bunchId.currentValue !== GuidEmpty;
      const manuallyChanged = changes.manually && this.bunchId;
      const pointChanged =
        changes.resultPoint?.currentValue &&
        (!this.isNBF || !changes.resultPoint.currentValue.needDeepSync || changes.resultPoint.currentValue.needRunSync) &&
        this.bunchId;

      if (bunchIsChanged || manuallyChanged || pointChanged) {
        this.resetComputersIfCreateOrNotFirstLoad();
        this.loadTreeData();
      }
    }
  }

  resetComputersIfCreateOrNotFirstLoad(): void {
    if (this.isCreate || !this.firstLoad) {
      if (this.stepForm) {
        this.stepForm.get('folders').markAsTouched();
        this.stepForm.get('folders').setValue([]);
      }

      return;
    }

    if (!this.isUnix || this.isNBF) return void (this.firstLoad = false);

    if (this.resultPoint) this.firstLoad = false;
  }

  updateForm(value: RestoreSourceStepValue): void {
    this.stepForm.reset(value);
  }

  forceValid(): void {
    if (this.isRDMode) this.fileFoldersComponent.updateValidate();
    FormsUtil.triggerValidation(this.stepForm);
  }

  showLegend(): void {
    const modal = this.modal.openRef(CheckboxLegendComponent, { isCustom: true });

    modal.componentInstance.isRestore = true;

    modal.result.then(noop).catch(noop);
  }

  ngOnDestroy(): void {
    this.stepService.treeData$?.next(null);
  }
}
